/**
 * Castings API Service
 * Handles all casting-related API calls
 */

import { apiClient } from './client';
import type { Casting, CreateCastingDto, CreatePublicCastingDto, UpdateCastingDto } from '@/types';

/**
 * Service for managing castings (convocatorias)
 * All endpoints are under /opportunities/castings
 */
export const castingsService = {
  /**
   * Get all castings
   * @returns Array of castings
   */
  async getAll(): Promise<Casting[]> {
    return apiClient.get<Casting[]>('/opportunities/castings');
  },

  /**
   * Get all castings for backoffice moderation (any review status)
   */
  async getAllForBackoffice(): Promise<Casting[]> {
    return apiClient.get<Casting[]>('/opportunities/castings/backoffice');
  },

  /**
   * Get a casting by ID for backoffice (any review status)
   */
  async getByIdForBackoffice(id: string): Promise<Casting> {
    return apiClient.get<Casting>(
      `/opportunities/castings/backoffice/${encodeURIComponent(id)}`,
    );
  },

  /**
   * Get a casting by ID
   * @param id - Casting ID
   * @returns Single casting
   * @throws {ApiError} If casting not found or server error
   */
  async getById(id: string): Promise<Casting> {
    return apiClient.get<Casting>(`/opportunities/castings/${encodeURIComponent(id)}`);
  },

  /**
   * Create a new casting
   * @param body - Casting data
   * @returns Created casting
   */
  async create(body: CreateCastingDto): Promise<Casting> {
    return apiClient.post<Casting>('/opportunities/castings', body);
  },

  /**
   * Submit a casting from the public form (pending moderation)
   */
  async createPublic(body: CreatePublicCastingDto): Promise<Casting> {
    return apiClient.post<Casting>('/opportunities/castings/public', body);
  },

  /**
   * Update an existing casting
   * @param id - Casting ID
   * @param body - Partial casting data
   * @returns Updated casting
   */
  async update(id: string, body: UpdateCastingDto): Promise<Casting> {
    return apiClient.patch<Casting>(
      `/opportunities/castings/${encodeURIComponent(id)}`,
      body,
    );
  },

  /**
   * Delete a casting
   * @param id - Casting ID
   * @returns Deleted casting
   */
  async delete(id: string): Promise<Casting> {
    return apiClient.delete<Casting>(
      `/opportunities/castings/${encodeURIComponent(id)}`,
    );
  },
};
